// Generic modal component that can be reused across the app
// with proper accessibility and focus management.
// Uses React portals to render the modal at the root level
// of the DOM to avoid z-index and overflow issues.
// Locks body scroll when the modal is open.
// Traps focus within the modal for accessibility.
// Closes on Escape key press and optionally on backdrop click.

// The children prop allows for flexible content inside the modal.
// Set the width and height of the modal via CSS in the children.
// The child root container cannot take up the full viewport to allow for backdrop click detection.
// Example usage:
// <GenericModal isOpen={isOpen} onClose={handleClose} title="My Modal">
//   <div className="w-96 p-4 bg-white rounded">
//   <h2 className="text-lg font-bold mb-4">Modal Title</h2>
//   <p>Modal content goes here.</p>
//   <button onClick={handleClose}>Close</button>
//   </div>
// </GenericModal>

import React from 'react'
import {createPortal} from 'react-dom'

type GenericModalProps = {
  isOpen: boolean
  onClose: () => void
  title?: string
  children: React.ReactNode
  closeOnBackdrop?: boolean
}

function useLockBodyScroll(lock: boolean) {
  // Lock body scroll when the modal is open
  React.useEffect(() => {
    if (!lock) return
    const original = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = original
    }
  }, [lock])
}

function trapFocus(e: KeyboardEvent, container: HTMLElement) {
  // Prevent focus from escaping the modal container
  const FOCUSABLE =
    'a[href], area[href], input:not([disabled]), select:not([disabled]), ' +
    'textarea:not([disabled]), button:not([disabled]), iframe, object, embed, ' +
    '[tabindex]:not([tabindex="-1"]), [contenteditable="true"]'

  const nodes = Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
    (el) => el.offsetParent !== null || el === document.activeElement
  )
  if (nodes.length === 0) return

  const first = nodes[0]
  const last = nodes[nodes.length - 1]
  const active = document.activeElement as HTMLElement | null

  if (!e.shiftKey && active === last) {
    e.preventDefault()
    first.focus()
  } else if (e.shiftKey && active === first) {
    e.preventDefault()
    last.focus()
  }
}

export default function GenericModal({
  isOpen,
  onClose,
  title,
  children,
  closeOnBackdrop = true,
}: GenericModalProps) {
  useLockBodyScroll(isOpen)
  const dialogRef = React.useRef<HTMLDivElement>(null)

  React.useEffect(() => {
    if (!isOpen) return
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
      if (e.key === 'Tab') trapFocus(e, dialogRef.current!)
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [isOpen, onClose])

  if (!isOpen) return null

  const body = (
    <div
      className="fixed inset-0 z-[1000] bg-black/40"
      aria-hidden={!isOpen}
      onClick={() => {
        if (closeOnBackdrop) onClose()
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        ref={dialogRef}
        tabIndex={-1}
        className={['fixed inset-0 outline-none', 'flex items-stretch justify-center'].join(' ')}
      >
        {children}
      </div>
    </div>
  )

  return createPortal(body, document.body)
}
